import { Injectable, inject } from '@angular/core';
import { GraphQLClient } from '../graphql/client';
import { MutationError, UserError } from '@branduals/graphql-errors';
import {
  REVOKE_SHARE_GRANT, SEARCH_SHARE_RECIPIENTS, SHARE_GRANTS, SHARE_INVITE, SHARE_WITH_MEMBERS,
  type RevokeShareGrantResponse, type SearchShareRecipientsResponse, type ShareGrantRow, type ShareGrantsResponse,
  type ShareInviteInput, type ShareInviteResponse,
  type ShareWithMembersInput, type ShareWithMembersResponse,
} from '../graphql/share-operations';
import type { BrandMemberSummary } from '../models/access.model';

export interface ShareWithMembersOutcome {
  shareId: string | null;
  success: boolean;
  errors: UserError[];
}

export interface ShareInviteOutcome {
  success: boolean;
  /** Recipients whose earlier same-subject invitation was refreshed (re-sent) instead of duplicated. */
  refreshedCount: number;
  /** Recipients who already accepted this subject's invitation and are waiting for an admin — nothing new was sent to them. */
  alreadyWaiting: string[];
  errors: UserError[];
}

export interface ShareGrantFilters {
  sharerUserId?: string | null;
  recipientUserId?: string | null;
}

export interface ShareGrantsPage {
  rows: ShareGrantRow[];
  totalCount: number;
  /** Brand-wide LIVE-grants count (filter-independent) — what the tab badge shows. */
  activeCount: number;
  hasNextPage: boolean;
  endCursor: string | null;
}

/**
 * Wire wrapper for the universal share flow. The dialog's two Send lanes RETURN
 * payload errors instead of throwing: a partially refused fan-out (some recipients
 * unavailable) is a rendered dialog state, so the caller needs the per-row errors
 * intact — only transport failures reject those promises. The admin ledger pair
 * (getShareGrants / revokeShareGrant) follows the standard service canon instead:
 * revoke throws MutationError so the hosting tab renders resolveErrorMessage copy.
 */
@Injectable({ providedIn: 'root' })
export class ShareService {
  private readonly graphql = inject(GraphQLClient);

  async shareWithMembers(input: ShareWithMembersInput): Promise<ShareWithMembersOutcome> {
    const response = await this.graphql.mutateAsync<ShareWithMembersResponse>(SHARE_WITH_MEMBERS, { input });
    const payload = response.shareWithMembers;
    return { shareId: payload.shareId, success: payload.success, errors: payload.errors ?? [] };
  }

  async shareInvite(input: ShareInviteInput): Promise<ShareInviteOutcome> {
    const response = await this.graphql.mutateAsync<ShareInviteResponse>(SHARE_INVITE, { input });
    const payload = response.shareInvite;
    return {
      success: payload.success,
      refreshedCount: payload.refreshedCount ?? 0,
      alreadyWaiting: payload.alreadyWaiting ?? [],
      errors: payload.errors ?? [],
    };
  }

  /** One page of the admin share-grants ledger (server-computed row state, newest first). */
  async getShareGrants(filters: ShareGrantFilters = {}, first = 50, after: string | null = null): Promise<ShareGrantsPage> {
    const response = await this.graphql.queryAsync<ShareGrantsResponse>(SHARE_GRANTS, {
      sharerUserId: filters.sharerUserId ?? null,
      recipientUserId: filters.recipientUserId ?? null,
      first,
      after,
    });
    const connection = response.shareGrants;
    return {
      rows: connection.nodes,
      totalCount: connection.totalCount,
      activeCount: connection.activeCount,
      hasNextPage: connection.pageInfo.hasNextPage,
      endCursor: connection.pageInfo.endCursor,
    };
  }

  async revokeShareGrant(shareGrantId: string): Promise<void> {
    const response = await this.graphql.mutateAsync<RevokeShareGrantResponse>(REVOKE_SHARE_GRANT, { shareGrantId });
    const payload = response.revokeShareGrant;
    if (payload.errors && payload.errors.length > 0) {
      throw new MutationError(payload.errors);
    }
  }

  /**
   * The dialog's people picker — the people a share can reach (active (approved, not paused), human,
   * mailbox-bearing members; the caller never offered), served by the one recipient-search home the @-mention
   * picker rides. An EMPTY query browses. A REFUSED read rejects, never an empty list: the
   * picker owns a two-state face (no match vs. couldn't search) that only its error arm can
   * raise, and a swallowed refusal would make that arm dead.
   */
  async searchRecipients(query: string, limit: number): Promise<BrandMemberSummary[]> {
    const response = await this.graphql.queryAsync<SearchShareRecipientsResponse>(SEARCH_SHARE_RECIPIENTS, { query, limit });
    return response.searchShareRecipients ?? [];
  }
}
